import React, { useCallback, useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useSelector } from 'react-redux';
import T from 'i18n';
import { selectCurrentCaseTeam, selectCurrentCaseTeamName } from 'common/selectors';
import { useFetchCrawlSummary } from '../../redux/fetchCrawlSummary';
import styles from '../CaseIntelligencePage.module.scss';
import { CrawlDocumentStatus, CrawlRunStatus, CrawlSummary } from '../types';
import {
  CRAWL_DURATION_WINDOW_MS,
  DOCUMENT_STATUS_ORDER,
  buildHeroSummary,
  formatDurationClock,
  formatDurationProgress,
  formatDurationSeconds,
  formatUpdatedLabel,
  formatUtcBlock,
  formatUtcInline,
  getDocumentStatusLabel,
  getDocumentStatusCount,
  getRunStatusLabel,
  getStatusPercentage,
  getTriggerLabel,
} from '../utils/crawlSummary';

interface Props {
  open: boolean;
  onClose: () => void;
}

const RUN_STATUS_CLASS: Record<CrawlRunStatus, string> = {
  completed: styles.drawerRunStatusCompleted,
  failed: styles.drawerRunStatusFailed,
  pending: styles.drawerRunStatusPending,
  running: styles.drawerRunStatusRunning,
};

const DOCUMENT_STATUS_CLASS: Record<CrawlDocumentStatus, string> = {
  completed: styles.drawerStatusCompleted,
  failed: styles.drawerStatusFailed,
  pending: styles.drawerStatusPending,
  processing: styles.drawerStatusProcessing,
  skipped: styles.drawerStatusSkipped,
};

const CrawlSummaryDrawer: React.FC<Props> = ({ open, onClose }) => {
  const currentCaseTeam = useSelector(selectCurrentCaseTeam);
  const currentCaseTeamName = useSelector(selectCurrentCaseTeamName);
  const [showScrollChrome, setShowScrollChrome] = useState(false);
  const scrollChromeTimeoutRef = useRef<number | null>(null);
  const {
    crawlSummary,
    crawlSummaryPending,
    crawlSummaryFeedback,
    crawlSummaryHint,
    fetchCrawlSummary,
  } = useFetchCrawlSummary();

  useEffect(() => {
    if (!open) {
      setShowScrollChrome(false);
      return;
    }

    fetchCrawlSummary().catch(() => {});
  }, [open, fetchCrawlSummary]);

  useEffect(() => {
    return () => {
      if (scrollChromeTimeoutRef.current) {
        window.clearTimeout(scrollChromeTimeoutRef.current);
      }
    };
  }, []);

  const showLoadingState = crawlSummaryPending && !crawlSummary;
  const showErrorState = !crawlSummary && Boolean(crawlSummaryFeedback?.error);
  const triggeredById = crawlSummary?.lastCrawl?.triggeredBy ?? null;
  const triggeredByUser = triggeredById
    ? (currentCaseTeam?.users || []).find(
        (user: any) => user?.id === triggeredById || user?.internalId === triggeredById,
      ) || null
    : null;
  const triggeredByName = triggeredById
    ? triggeredByUser?.name ||
      triggeredByUser?.mail ||
      (T.translate('caseIntelligence.unknownUser') as string)
    : '-';

  const handleDrawerScroll = useCallback(() => {
    setShowScrollChrome(true);

    if (scrollChromeTimeoutRef.current) {
      window.clearTimeout(scrollChromeTimeoutRef.current);
    }

    scrollChromeTimeoutRef.current = window.setTimeout(() => {
      setShowScrollChrome(false);
      scrollChromeTimeoutRef.current = null;
    }, 900);
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={0}
      classes={{ paper: styles.crawlSummaryDrawerPaper }}
    >
      <div
        className={`${styles.crawlSummaryDrawerContent} ${
          showScrollChrome ? styles.crawlSummaryDrawerContentScrolling : ''
        }`}
        onScroll={handleDrawerScroll}
      >
        <div className={styles.crawlSummaryDrawerHeader}>
          <div className={styles.drawerTitleRow}>
            <div className={styles.drawerEyebrow}>
              {T.translate('caseIntelligence.crawlSummaryTitle', {
                defaultValue: 'Crawl Summary',
              })}
            </div>
            {crawlSummaryPending && crawlSummary ? (
              <div className={styles.drawerPendingBadge}>
                <CircularProgress size={14} />
                <span>
                  {T.translate('caseIntelligence.refreshingCrawlSummary', {
                    defaultValue: 'Refreshing',
                  })}
                </span>
              </div>
            ) : null}
          </div>
          <IconButton
            aria-label={
              T.translate('caseIntelligence.closeCrawlSummary', {
                defaultValue: 'Close crawl summary',
              }) as string
            }
            className={styles.drawerCloseButton}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {showLoadingState ? (
          <div className={styles.drawerStatePanel}>
            <CircularProgress size={22} />
            <span>
              {T.translate('caseIntelligence.loadingCrawlSummary', {
                defaultValue: 'Loading crawl summary',
              })}
            </span>
          </div>
        ) : showErrorState ? (
          <div className={styles.drawerErrorPanel} role="alert">
            <WarningAmberOutlinedIcon />
            <div>
              <div className={styles.drawerErrorTitle}>
                {T.translate('caseIntelligence.crawlSummaryLoadError', {
                  defaultValue: 'Unable to load crawl summary.',
                })}
              </div>
              <div className={styles.drawerErrorCopy}>
                {T.translate('caseIntelligence.crawlSummaryRetryHint', {
                  defaultValue: 'Close and reopen the drawer to try again.',
                })}
              </div>
            </div>
          </div>
        ) : !crawlSummary || !crawlSummary.lastCrawl ? (
          <div className={styles.drawerStatePanel}>
            <span>
              {crawlSummaryHint ||
                T.translate('caseIntelligence.noCrawlSummary', {
                  defaultValue: 'No crawl summary is available yet.',
                })}
            </span>
          </div>
        ) : (
          <CrawlSummaryDrawerBody
            error={crawlSummaryFeedback?.error ? String(crawlSummaryFeedback.error) : null}
            lastCrawl={crawlSummary.lastCrawl}
            summary={crawlSummary}
            teamName={currentCaseTeamName}
            triggeredById={triggeredById}
            triggeredByName={triggeredByName}
          />
        )}
      </div>
    </Drawer>
  );
};

interface BodyProps {
  error: string | null;
  lastCrawl: NonNullable<CrawlSummary['lastCrawl']>;
  summary: CrawlSummary;
  teamName: string | null;
  triggeredById: string | null;
  triggeredByName: string;
}

const CrawlSummaryDrawerBody: React.FC<BodyProps> = ({
  error,
  summary,
  lastCrawl,
  teamName,
  triggeredById,
  triggeredByName,
}) => {
  const { stats } = summary;
  const startedAt = formatUtcBlock(lastCrawl.startedAt);
  const completedAt = formatUtcBlock(lastCrawl.completedAt);
  const completedPercent = getStatusPercentage(
    getDocumentStatusCount(stats, 'completed'),
    stats.totalDocuments,
  );
  const durationPercent = formatDurationProgress(stats.totalDurationMs);

  return (
    <>
      {error ? (
        <div className={styles.drawerInlineError} role="alert">
          {T.translate('caseIntelligence.crawlSummaryRefreshFailed', {
            defaultValue: 'Latest refresh failed. Showing the last available crawl summary.',
          })}
        </div>
      ) : null}

      <section className={styles.drawerCard}>
        <p className={styles.drawerHeroDescription}>{buildHeroSummary(summary)}</p>
        <div className={styles.drawerChipRow}>
          <MetricChip tone="blue" value={formatUpdatedLabel(summary.lastArtefactGeneration)} />
          <MetricChip
            tone="blue"
            value={`${stats.totalDocuments} ${T.translate('caseIntelligence.documentsPlural')}`}
          />
          <MetricChip
            tone={getDocumentStatusCount(stats, 'failed') > 0 ? 'red' : 'green'}
            value={`${getDocumentStatusCount(stats, 'failed')} ${T.translate('caseIntelligence.failures')}`}
          />
          <MetricChip
            tone="orange"
            value={`${formatDurationSeconds(stats.totalDurationMs)} ${T.translate('caseIntelligence.durationLabel')}`}
          />
          <MetricChip
            tone="blue"
            value={`${stats.totalExtractions} ${stats.totalExtractions === 1 ? T.translate('caseIntelligence.extraction') : T.translate('caseIntelligence.extractions')}`}
          />
        </div>
      </section>

      <div className={styles.drawerSectionLabel}>
        {T.translate('caseIntelligence.lastCrawl', { defaultValue: 'Last Crawl' })}
      </div>
      <section className={styles.drawerCard}>
        <div className={styles.lastCrawlHeader}>
          <div className={styles.lastCrawlSummary}>
            <div className={styles.lastCrawlHeading}>
              <div className={styles.lastCrawlTitle}>{getTriggerLabel(lastCrawl.triggerType)}</div>
              <RunStatusChip status={lastCrawl.status} />
            </div>
            <div className={styles.lastCrawlMetaPanel}>
              <div className={styles.lastCrawlMetaBlock}>
                <div className={styles.lastCrawlMetaLabel}>
                  {T.translate('caseIntelligence.triggeredBy')}
                </div>
                <div className={styles.lastCrawlMetaLine}>
                  <span className={styles.lastCrawlMetaKey}>
                    {T.translate('caseIntelligence.userName')}
                  </span>
                  <span className={styles.lastCrawlMetaValue}>{triggeredByName}</span>
                </div>
                <div className={styles.lastCrawlMetaLine}>
                  <span className={styles.lastCrawlMetaKey}>
                    {T.translate('caseIntelligence.userId')}
                  </span>
                  <span className={styles.lastCrawlMetaValue}>{triggeredById || '-'}</span>
                </div>
              </div>
              <div className={styles.lastCrawlMetaBlock}>
                <div className={styles.lastCrawlMetaLabel}>
                  {T.translate('caseIntelligence.crawlId')}
                </div>
                <div className={styles.lastCrawlMetaLine}>
                  <span className={styles.lastCrawlMetaSecondary}>{lastCrawl.crawlId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.lastCrawlTimeline}>
          <MetricPanel
            label={T.translate('caseIntelligence.startedAt', { defaultValue: 'Started' })}
            primary={startedAt.time}
            secondary={startedAt.date}
          />
          <MetricPanel
            label={T.translate('caseIntelligence.completedAt', { defaultValue: 'Completed' })}
            primary={
              lastCrawl.completedAt
                ? completedAt.time
                : (T.translate('caseIntelligence.inProgress') as string)
            }
            secondary={lastCrawl.completedAt ? completedAt.date : '-'}
          />
          <MetricPanel
            label={T.translate('caseIntelligence.totalDuration', {
              defaultValue: 'Total Duration',
            })}
            primary={formatDurationClock(stats.totalDurationMs)}
            secondary={`${stats.totalDurationMs.toLocaleString()} ms`}
          />
        </div>

        <div className={styles.lastCrawlDuration}>
          <div className={styles.lastCrawlDurationHeader}>
            <span>
              {T.translate('caseIntelligence.crawlDurationWindow', {
                defaultValue: 'Crawl duration (relative to 10 min window)',
              })}
            </span>
            <strong>
              {formatDurationSeconds(stats.totalDurationMs)} /{' '}
              {Math.round(CRAWL_DURATION_WINDOW_MS / 1000)}s
            </strong>
          </div>
          <div className={styles.durationTrack}>
            <div className={styles.durationFill} style={{ width: `${durationPercent}%` }} />
          </div>
        </div>
      </section>

      <div className={styles.drawerSectionLabel}>
        {T.translate('caseIntelligence.processingOverview', {
          defaultValue: 'Processing Overview',
        })}
      </div>
      <div className={styles.overviewGrid}>
        <OverviewCard
          label={T.translate('caseIntelligence.totalDocuments', {
            defaultValue: 'Total Documents',
          })}
          tone="primary"
          value={stats.totalDocuments}
          helper={T.translate('caseIntelligence.totalDocumentsHelper', {
            defaultValue: 'in corpus',
          })}
        />
        <OverviewCard
          label={T.translate('caseIntelligence.completed', { defaultValue: 'Completed' })}
          tone="success"
          value={getDocumentStatusCount(stats, 'completed')}
          helper={`${completedPercent} ${T.translate('caseIntelligence.ofCorpus')}`}
        />
        <OverviewCard
          label={T.translate('caseIntelligence.skipped', { defaultValue: 'Skipped' })}
          tone="muted"
          value={getDocumentStatusCount(stats, 'skipped')}
          helper={T.translate('caseIntelligence.skippedHelper', {
            defaultValue: 'unchanged',
          })}
        />
        <OverviewCard
          label={T.translate('caseIntelligence.totalExtractions', {
            defaultValue: 'Extractions',
          })}
          tone="warning"
          value={stats.totalExtractions}
          helper={`${T.translate('caseIntelligence.docsReplaced')} ${stats.documentsReplaced}`}
        />
      </div>

      <div className={styles.drawerBreakdownGrid}>
        <section className={styles.drawerCard}>
          <div className={styles.breakdownTitle}>
            {T.translate('caseIntelligence.documentStatusBreakdown', {
              defaultValue: 'Document Status Breakdown',
            })}
          </div>
          <div className={styles.statusBreakdownTrack}>
            {summary.stats.totalDocuments > 0
              ? DOCUMENT_STATUS_ORDER.map((status) => {
                  const count = getDocumentStatusCount(stats, status);

                  if (count <= 0) {
                    return null;
                  }

                  return (
                    <div
                      key={status}
                      className={`${styles.statusBreakdownSegment} ${DOCUMENT_STATUS_CLASS[status]}`}
                      style={{ flexGrow: count }}
                    />
                  );
                })
              : null}
          </div>

          <div className={styles.breakdownRows}>
            {DOCUMENT_STATUS_ORDER.map((status) => {
              const count = getDocumentStatusCount(stats, status);

              return (
                <div className={styles.breakdownRow} key={status}>
                  <div className={styles.breakdownRowLabel}>
                    <span className={`${styles.breakdownDot} ${DOCUMENT_STATUS_CLASS[status]}`} />
                    <span>{getDocumentStatusLabel(status)}</span>
                  </div>
                  <div className={styles.breakdownRowCount}>{count}</div>
                  <div className={styles.breakdownRowPercent}>
                    {getStatusPercentage(count, stats.totalDocuments)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className={styles.drawerCard}>
          <div className={styles.breakdownTitle}>
            {T.translate('caseIntelligence.crawlMetadata', {
              defaultValue: 'Crawl Metadata',
            })}
          </div>
          <div className={styles.metadataRows}>
            <MetadataRow
              label={T.translate('caseIntelligence.caseId') as string}
              value={summary.caseId}
              accent
            />
            <MetadataRow
              label={T.translate('caseIntelligence.type') as string}
              value={summary.type}
            />
            <MetadataRow
              label={T.translate('caseIntelligence.team') as string}
              value={
                <div className={styles.metadataValueStack}>
                  <span>{teamName || '-'}</span>
                  <span className={styles.metadataValueSubtle}>
                    {T.translate('caseIntelligence.teamId')} {summary.teamId}
                  </span>
                </div>
              }
              muted
            />
            <MetadataRow
              label={T.translate('caseIntelligence.tokensUsedMeta') as string}
              value={stats.totalTokensUsed.toLocaleString()}
            />
            <MetadataRow
              label={T.translate('caseIntelligence.docsRemoved') as string}
              value={stats.documentsRemoved}
            />
            <MetadataRow
              label={T.translate('caseIntelligence.docsReplacedMeta') as string}
              value={stats.documentsReplaced}
              positive
            />
            <MetadataRow
              label={T.translate('caseIntelligence.lastArtefactGen') as string}
              value={formatUtcInline(summary.lastArtefactGeneration)}
            />
            <MetadataRow
              label={T.translate('caseIntelligence.failureReason') as string}
              value={lastCrawl.failureReason || '-'}
              muted
            />
          </div>
        </section>
      </div>
    </>
  );
};

interface MetricChipProps {
  tone: 'blue' | 'green' | 'orange' | 'red';
  value: string;
}

const MetricChip: React.FC<MetricChipProps> = ({ tone, value }) => {
  const toneClass =
    tone === 'green'
      ? styles.metricChipGreen
      : tone === 'orange'
        ? styles.metricChipOrange
        : tone === 'red'
          ? styles.metricChipRed
          : styles.metricChipBlue;

  return <div className={`${styles.drawerMetricChip} ${toneClass}`}>{value}</div>;
};

interface RunStatusChipProps {
  status: CrawlRunStatus;
}

const RunStatusChip: React.FC<RunStatusChipProps> = ({ status }) => (
  <div className={`${styles.runStatusChip} ${RUN_STATUS_CLASS[status]}`}>
    <span className={styles.runStatusDot} />
    <span>{getRunStatusLabel(status)}</span>
  </div>
);

interface MetricPanelProps {
  label: string;
  primary: string;
  secondary: string;
}

const MetricPanel: React.FC<MetricPanelProps> = ({ label, primary, secondary }) => (
  <div className={styles.timelineMetricPanel}>
    <div className={styles.timelineMetricLabel}>{label}</div>
    <div className={styles.timelineMetricPrimary}>{primary}</div>
    <div className={styles.timelineMetricSecondary}>{secondary}</div>
  </div>
);

interface OverviewCardProps {
  helper: string;
  label: string;
  tone: 'muted' | 'primary' | 'success' | 'warning';
  value: number;
}

const OverviewCard: React.FC<OverviewCardProps> = ({ helper, label, tone, value }) => {
  const toneClass =
    tone === 'success'
      ? styles.overviewValueSuccess
      : tone === 'warning'
        ? styles.overviewValueWarning
        : tone === 'muted'
          ? styles.overviewValueMuted
          : styles.overviewValuePrimary;

  return (
    <section className={styles.overviewCard}>
      <div className={styles.overviewCardLabel}>{label}</div>
      <div className={`${styles.overviewCardValue} ${toneClass}`}>{value}</div>
      <div className={styles.overviewCardHelper}>{helper}</div>
    </section>
  );
};

interface MetadataRowProps {
  accent?: boolean;
  label: string;
  muted?: boolean;
  positive?: boolean;
  value: React.ReactNode;
}

const MetadataRow: React.FC<MetadataRowProps> = ({
  accent = false,
  label,
  muted = false,
  positive = false,
  value,
}) => {
  const valueClass = accent
    ? styles.metadataValueAccent
    : positive
      ? styles.metadataValuePositive
      : muted
        ? styles.metadataValueMuted
        : styles.metadataValue;

  return (
    <div className={styles.metadataRow}>
      <div className={styles.metadataLabel}>{label}</div>
      <div className={valueClass}>{value}</div>
    </div>
  );
};

export default CrawlSummaryDrawer;
